<script setup lang="ts">
import { AppRoute, followedRouteId } from '@/libs/routing';
import MapCanvas from './MapCanvas.vue';
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppStore, type LatLong, type MapFocus } from '@/stores/app';
import { useGtfsStore, type Bounds } from '@/stores/gtfs';
import type { OnlineDevice } from '@/@types/device';
import { LocationType, type Route, type Shape, type Stop } from '@/@types/gtfs-ressource';
import type { MapRessourceClicked, MapShape, MapStop, MapVehicle } from '@/@types/map';
import { MapLabelPriority, MapZoomLevel } from '@/@types/map';
import { useDevicesStore, type DeviceFilter } from '@/stores/devices';
import { useTripUpdatesStore } from '@/stores/trip-updates';
import { storeToRefs } from 'pinia';
import { StopType } from '@/@types/trip-updates';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import { isCanonicalStop } from '@/libs/helpers/stop-clusters';
import { useJourneyStore } from '@/stores/journey';
import { useOnDemandStore } from '@/stores/on-demand';
import type { OnDemandRoute } from '@/@types/on-demand-route';
import { isServingOnDemandRoute } from '@/libs/on-demand-helper';
import type { Journey, TransitLeg } from '@/libs/journey/journeys';
import { endMarkers, placeMarker, walkShape, type JourneyEnd } from './journey-markers';
import { vehicleAnimationOf } from '@/use/vehicle-animation';

const gtfsStore = useGtfsStore();
const devicesStore = useDevicesStore();
const appStore = useAppStore();
const tripUpStore = useTripUpdatesStore();
const journeyStore = useJourneyStore();
const onDemandStore = useOnDemandStore();
const { getUpdatedShape, getTripUpdateStops, currentTripTerminusIds } = storeToRefs(tripUpStore);

const router = useRouter();
const route = useRoute();

const routeName = computed<string>(() => route.name as string);
const selectedRouteId = computed<string | undefined>(() => route.params.routeId as string);
const selectedTripId = computed<string | undefined>(() => route.params.tripId as string);
const selectedStopId = computed<string | undefined>(() => route.params.stopId as string);
const selectedOnDemandRouteId = computed<string | undefined>(() => route.params.onDemandRouteId as string);
const selectedStopDevices = computed<string[]>(() => appStore.selectedStop.devices);
// Matched against the URL: right after a navigation the store still holds the previous route,
// and MapLibre drops a source update that lands right after another one.
const shownOnDemandRoute = computed<OnDemandRoute | undefined>(() =>
  onDemandStore.shownRoute?.route_id === selectedOnDemandRouteId.value ? onDemandStore.shownRoute : undefined,
);

const mapFocus = computed<MapFocus | undefined>(() => appStore.mapFocus);
const networkMapBounds = computed<Bounds>(() => gtfsStore.networkMapBounds);
const positionGeolocation = computed<LatLong | undefined>(() => appStore.positionGeolocation);
const isGeolocated = computed<boolean>(() => Boolean(positionGeolocation.value));

const devices = computed<Array<OnlineDevice>>(() => devicesStore.onlineDevices);
const routes = computed<{ [key: string]: Route }>(() => gtfsStore.gtfsData.routes);
const shapes = computed<{ [key: string]: Shape }>(() => gtfsStore.gtfsData.shapes);
const stations = computed<{ [key: string]: Array<string> }>(() => gtfsStore.stations);
const shownStops = computed<{ [key: string]: Stop }>(() => gtfsStore.shownStops);
const terminus = computed<{ [key: string]: boolean } | undefined>(() => gtfsStore.gtfsData.terminus);

/** The journey followed from the itinerary: the map then draws it alone. */
const trackedJourney = computed<Journey | undefined>(() =>
  routeName.value === AppRoute.ITINERARY ? journeyStore.tracked?.journey : undefined,
);
const trackedRides = computed<TransitLeg[]>(
  () => trackedJourney.value?.legs.filter((leg): leg is TransitLeg => leg.type === 'transit') ?? [],
);

const routeColor = (routeId: string | undefined) =>
  `#${(routeId && routes.value[routeId]?.route_color) || 'f0f0f0'}`;

const mapWalks = computed<Array<MapShape>>(
  () =>
    trackedJourney.value?.legs.flatMap(leg =>
      leg.type === 'walk' && leg.distance > 0 ? [walkShape(leg)] : [],
    ) ?? [],
);

const mapOldShape = computed<MapShape | undefined>(() => {
  if (trackedJourney.value) return undefined;
  if (!getUpdatedShape.value && Object.values(gtfsStore.shownShapes).length === 1) return undefined;
  const shape = Object.values(gtfsStore.shownShapes)[0];
  if (!shape) return undefined;
  const route = shape.routeId ? routes.value[shape.routeId] : null;
  const color = route?.route_color ? route.route_color : 'f0f0f0';
  return {
    geometry: shape.geometry,
    properties: {
      routeId: shape.routeId,
      color: `#${color}`,
    },
  };
});

const mapShapes = computed<Array<MapShape>>(() => {
  if (trackedJourney.value) {
    return trackedRides.value.map(ride => ({
      geometry: { type: 'LineString', coordinates: ride.path },
      properties: { routeId: ride.routeId, color: routeColor(ride.routeId) },
    }));
  }
  return Object.values(gtfsStore.shownShapes).map(shape => {
    const route = shape.routeId ? routes.value[shape.routeId] : null;
    const color = route?.route_color ? route.route_color : 'f0f0f0';

    const geometry = getUpdatedShape.value
      ? { ...shape.geometry, ...{ coordinates: getUpdatedShape.value } }
      : shape.geometry;
    return {
      geometry: geometry,
      properties: {
        routeId: shape.routeId,
        color: `#${color}`,
      },
    };
  });
});

// Only the arrival of the trip is drawn as a pin; the departure is a regular stop.
const tripArrivalStopId = computed<string | undefined>(
  () => currentTripTerminusIds.value[currentTripTerminusIds.value.length - 1],
);
const tripDepartureStopId = computed<string | undefined>(() => currentTripTerminusIds.value[0]);

// Route view: both ends of the line are drawn as pins and keep their name.
const routeEndStopIds = computed<Array<string>>(() =>
  selectedRouteId.value ? gtfsStore.getRouteEndStopIds(selectedRouteId.value) : [],
);

function journeyPoint(end: JourneyEnd): MapStop | undefined {
  const place = end === 'origin' ? journeyStore.origin : journeyStore.destination;
  return place?.kind === 'point' ? placeMarker(place, end, gtfsStore.gtfsData.stops) : undefined;
}

const chosenEnds = () => endMarkers(journeyStore.origin, journeyStore.destination, gtfsStore.gtfsData.stops);

/** While the itinerary is being searched, the map shows its chosen ends alone, named. */
const itineraryEnds = computed<{ [key: string]: MapStop } | undefined>(() => {
  if (routeName.value !== AppRoute.ITINERARY || trackedJourney.value) return undefined;
  const ends = chosenEnds();
  return Object.keys(ends).length > 0 ? ends : undefined;
});

/**
 * Stops of the on-demand route shown, all named, both ends as pins. Their ids belong
 * to the route, not to the GTFS: nothing here may look them up among the GTFS stops.
 */
function onDemandStops(): { [key: string]: MapStop } {
  const stops = shownOnDemandRoute.value?.stops ?? [];
  const mapStops: { [key: string]: MapStop } = {};
  stops.forEach((stop, index) => {
    const isDeparture = index === 0;
    const isArrival = stops.length > 1 && index === stops.length - 1;
    mapStops[stop.stop_id] = {
      id: stop.stop_id,
      name: stop.stop_name,
      position: { lng: stop.stop_lon, lat: stop.stop_lat },
      parentStation: '',
      terminus: isDeparture || isArrival,
      zoomLevel: { stops: true, stations: true, terminus: true },
      children: [],
      type: StopType.REGULAR,
      labelPriority: isArrival
        ? MapLabelPriority.ARRIVAL
        : isDeparture
          ? MapLabelPriority.DEPARTURE
          : MapLabelPriority.DEFAULT,
    };
  });
  return mapStops;
}

/**
 * Boarding, alighting and transfer stops of the journey followed, and the addresses
 * at its ends: the arrival is a pin, the last stop when the journey ends there.
 */
function journeyStops(rides: TransitLeg[]): { [key: string]: MapStop } {
  // Walking the whole way: no stop on the way, only its two ends.
  if (rides.length === 0) return chosenEnds();
  const mapStops: { [key: string]: MapStop } = {};
  const lastRide = rides[rides.length - 1];
  const origin = journeyPoint('origin');
  const destination = journeyPoint('destination');
  rides.forEach((ride, index) => {
    [ride.boardStopId, ride.alightStopId].forEach(stopId => {
      const stop = gtfsStore.gtfsData.stops[stopId];
      if (!stop) return;
      const isArrival = !destination && ride === lastRide && stopId === ride.alightStopId;
      const isDeparture = !origin && index === 0 && stopId === ride.boardStopId;
      mapStops[stopId] = {
        id: stopId,
        name: stop.stop_name,
        position: { lng: stop.stop_lon, lat: stop.stop_lat },
        parentStation: stop.parent_station,
        terminus: isArrival,
        zoomLevel: { stops: true, stations: true, terminus: true },
        children: [],
        type: StopType.REGULAR,
        labelPriority: isArrival
          ? MapLabelPriority.ARRIVAL
          : isDeparture
            ? MapLabelPriority.DEPARTURE
            : MapLabelPriority.DEFAULT,
      };
    });
  });
  [origin, destination].forEach(point => point && (mapStops[point.id] = point));
  return mapStops;
}

const mapStops = computed<{ [key: string]: MapStop }>(() => {
  if (trackedJourney.value) return journeyStops(trackedRides.value);
  if (itineraryEnds.value) return itineraryEnds.value;
  if (selectedOnDemandRouteId.value) return onDemandStops();
  const mapStops: { [key: string]: MapStop } = {};

  // eslint-disable-next-line compat/compat -- iOS 11 is below our real support floor (plugin-legacy targets 'defaults, not IE 11'); .browserslistrc is intentionally broader.
  const tripUpdateStops = Object.fromEntries(getTripUpdateStops.value);
  // Specific case : on trip-detailed, shownStops come from tripUpdateStore instead of gtfsStore
  const isTripView = Object.values(tripUpdateStops).length > 0;
  const stopsToIterate = isTripView ? tripUpdateStops : shownStops.value;

  Object.values(stopsToIterate).forEach((stop: Stop) => {
    mapStops[stop.stop_id] = {
      id: stop.stop_id,
      name: stop.stop_name,
      position: {
        lng: stop.stop_lon,
        lat: stop.stop_lat,
      },
      parentStation: stop.parent_station,
      terminus: isTripView
        ? stop.stop_id === tripArrivalStopId.value
        : routeEndStopIds.value.includes(stop.stop_id),
      zoomLevel: {},
      children: stations.value[stop.stop_id] || [],
      type: stop.type ?? StopType.REGULAR,
    };
    // If case trip update (from trip-detailed) : display all stops in map
    if (isTripView) {
      mapStops[stop.stop_id].zoomLevel = { stops: true, stations: true, terminus: true };
      // Where the names cannot all fit, the two ends of the trip keep theirs.
      if (stop.stop_id === tripArrivalStopId.value) {
        mapStops[stop.stop_id].labelPriority = MapLabelPriority.ARRIVAL;
      } else if (stop.stop_id === tripDepartureStopId.value) {
        mapStops[stop.stop_id].labelPriority = MapLabelPriority.DEPARTURE;
      }
    } else {
      const routeEnd = routeEndStopIds.value.indexOf(stop.stop_id);
      if (routeEnd === 0) mapStops[stop.stop_id].labelPriority = MapLabelPriority.DEPARTURE;
      else if (routeEnd > 0) mapStops[stop.stop_id].labelPriority = MapLabelPriority.ARRIVAL;
      // Else default logic
      if (stop.location_type !== LocationType.STATION) {
        mapStops[stop.stop_id].zoomLevel.stops = true;
      }
      if (isCanonicalStop(stop.stop_id, gtfsStore.canonicalOf)) {
        mapStops[stop.stop_id].zoomLevel.stations = true;
      }
      if (terminus.value?.[stop.stop_id]) {
        mapStops[stop.stop_id].zoomLevel.terminus = true;
      }
    }
  });

  return mapStops;
});

const mapVehicles = computed<{ [key: string]: MapVehicle }>(() => {
  const mapVehicles: { [key: string]: MapVehicle } = {};
  if (selectedOnDemandRouteId.value) return onDemandVehicles(selectedOnDemandRouteId.value);
  let vFilter: (v: OnlineDevice) => boolean;

  if (trackedJourney.value) {
    const tripIds = trackedRides.value.map(ride => ride.tripId);
    vFilter = (v: OnlineDevice) => Boolean(v.trip_id && tripIds.includes(v.trip_id));
  } else if (selectedRouteId.value) {
    vFilter = (v: OnlineDevice) => v.route_id === selectedRouteId.value;
  } else if (selectedStopId.value) {
    vFilter = (v: OnlineDevice) => selectedStopDevices.value.indexOf(v.device_id) >= 0;
  } else if (selectedTripId.value) {
    vFilter = (v: OnlineDevice) => v.trip_id === selectedTripId.value;
  } else {
    return mapVehicles;
  }

  if (devices.value) {
    Object.values(devices.value)
      .filter(v => v.latlng)
      .filter(vFilter)
      .filter(v => !v.trip_pending)
      .forEach(vehicle => {
        const vehicleRoute = vehicle.route_id ? routes.value[vehicle.route_id] : undefined;
        const color = vehicleRoute?.route_color ? vehicleRoute.route_color : 'f0f0f0';
        mapVehicles[vehicle.device_id] = {
          id: vehicle.device_id,
          position: {
            lng: vehicle.latlng[1],
            lat: vehicle.latlng[0],
          },
          bearing: vehicle.bearing || 0,
          color: '#' + color,
          selected: vehicle.trip_id === route.params.tripId,
          animation: vehicleAnimationOf(
            vehicle,
            gtfsStore.gtfsData,
            getUpdatedShape.value ? selectedTripId.value : undefined,
          ),
        };
      });
  }

  return mapVehicles;
});

/** In the colour of their line, heading deduced from their positions, dimmed once no longer live. */
function onDemandVehicles(onDemandRouteId: string): { [key: string]: MapVehicle } {
  const mapVehicles: { [key: string]: MapVehicle } = {};
  const color = `#${shownOnDemandRoute.value?.route_color || 'f0f0f0'}`;
  devices.value
    .filter(device => device.latlng && isServingOnDemandRoute(device, onDemandRouteId))
    .forEach(device => {
      mapVehicles[device.device_id] = {
        id: device.device_id,
        position: { lng: device.latlng[1], lat: device.latlng[0] },
        bearing: device.bearing,
        color,
        selected: false,
        stale: onDemandStore.vehiclesStale,
      };
    });
  return mapVehicles;
}

watch([() => routeName.value, () => shapes.value], () => {
  handleMapRoute();
});

watch(
  selectedRouteId,
  () => {
    if (selectedRouteId.value) initNewOnlineDevicePolling({ routeIds: [selectedRouteId.value] });
  },
  { immediate: true },
);

watch(
  selectedOnDemandRouteId,
  () => {
    if (selectedOnDemandRouteId.value) {
      initNewOnlineDevicePolling({ onDemandRouteIds: [selectedOnDemandRouteId.value] });
    }
  },
  { immediate: true },
);

watch(
  selectedTripId,
  () => {
    if (selectedTripId.value) initNewOnlineDevicePolling({ tripId: selectedTripId.value });
  },
  { immediate: true },
);

watch(
  selectedStopId,
  () => {
    if (selectedStopId.value) {
      const stopIds = gtfsStore.stations[selectedStopId.value] || [selectedStopId.value];
      const stopTimes = gtfsStore.getStopTimes(stopIds);
      const routeIds = [...new Set(stopTimes.map(st => st.route_id))].filter(Boolean);
      if (routeIds.length > 0) {
        initNewOnlineDevicePolling({ routeIds });
      } else {
        devicesStore.stopGetDevices();
      }
    }
  },
  { immediate: true },
);

function getUserPosition() {
  appStore.getGeolocation();
}

function handleMapRoute() {
  if (routeName.value === AppRoute.MAP) {
    // Fix update shape & stops with timeout in order to avoid non unmounted page at routeChange
    // that re-write those datas
    setTimeout(() => {
      // A stop or route opened meanwhile has already shown its own, and only pushes
      // them again once they change.
      if (routeName.value !== AppRoute.MAP) return;
      gtfsStore.setShownStops(gtfsStore.gtfsData.stops);
      gtfsStore.setShownShapes(gtfsStore.gtfsData.shapes);
    }, 500);
  }
  // Not on AppRoute.MAP: the home sheet polls the devices of its stops' routes.
  if (
    [AppRoute.ROUTE_LIST, AppRoute.STOP_LIST, AppRoute.PASSENGER_MESSAGE, AppRoute.SETTINGS].includes(
      routeName.value,
    )
  ) {
    devicesStore.stopGetDevices();
  }
}

function onMapClick(event: MapRessourceClicked) {
  // Neither its stops (not GTFS ones) nor its vehicles (no trip, no ETA) open anything.
  if (selectedOnDemandRouteId.value) return;
  // An end or a step of the journey: opening its stop would leave the search.
  const isJourneyMarker = Boolean(itineraryEnds.value || trackedJourney.value);
  if (event.stopId && (isJourneyMarker || !gtfsStore.gtfsData.stops[event.stopId])) return;
  if (event.stopId) {
    // Tapped while following a line, the stop stays narrowed to it, like the panel's stop list.
    const routeId = followedRouteId(route);
    router.push({
      name: AppRoute.STOP_DETAILED,
      params: { stopId: event.stopId },
      query: routeId ? { route: routeId } : undefined,
    });
  } else if (event.vehicleId) {
    const device = devices.value.find(device => device.device_id === event.vehicleId);

    if (device && device.trip_id) {
      router.push({ name: AppRoute.TRIP_DETAILED, params: { tripId: device.trip_id } });
    }
  } else if (event.routeId) {
    router.push({ name: AppRoute.ROUTE_DETAILED, params: { routeId: event.routeId } });
  }
}

function onMapZoomLevelChange(zoomLevel: MapZoomLevel) {
  if (zoomLevel === MapZoomLevel.STOPS || !selectedStopId.value) return;

  const canonicalId = gtfsStore.getCanonicalStopId(selectedStopId.value);
  if (canonicalId !== selectedStopId.value && gtfsStore.gtfsData.stops[canonicalId]) {
    // `?route=` survives: the stop stays narrowed to the line the passenger follows
    router.push({ name: AppRoute.STOP_DETAILED, params: { stopId: canonicalId }, query: route.query });
  }
}

// Restarting supersedes the previous polling without emptying the list first:
// the vehicle just tapped stays on the map until the new filters answer,
// instead of blinking out for a whole round trip.
function initNewOnlineDevicePolling(params: DeviceFilter = {}) {
  devicesStore.startGetDevices(params);
}
</script>

<template>
  <div>
    <!-- On the home, the search bar overlays the top of the map: push the actions below it.
         The itinerary fields take that corner over. -->
    <div
      v-if="routeName !== AppRoute.ITINERARY"
      class="map__actions"
      :class="{ 'map__actions--below-search': routeName === AppRoute.MAP }"
    >
      <button
        class="map__action-button"
        type="button"
        :aria-label="$t('map.locateMe')"
        @click="getUserPosition"
      >
        <span v-if="isGeolocated" class="map__position-indicator" />
        <BaseIcon v-else name="map-pin" />
      </button>
      <button
        class="map__action-button"
        type="button"
        :aria-label="$t('map.recenter')"
        @click="appStore.refocusMap"
      >
        <BaseIcon name="focus" />
      </button>
    </div>
    <MapCanvas
      :focus="mapFocus"
      :network-bounds="networkMapBounds"
      :selected-stop-id="selectedStopId"
      :show-stop-names="
        Boolean(
          selectedTripId ||
          selectedStopId ||
          selectedRouteId ||
          selectedOnDemandRouteId ||
          trackedJourney ||
          itineraryEnds,
        )
      "
      :shapes="mapShapes"
      :walks="mapWalks"
      :map-old-shape="mapOldShape"
      :stops="mapStops"
      :trip-view="Boolean(selectedTripId || trackedJourney)"
      :user-position="positionGeolocation"
      :vehicles="mapVehicles"
      :picking="appStore.isPickingOnMap"
      @pick="appStore.setMapPickedPoint"
      @click="onMapClick"
      @zoomLevelChange="onMapZoomLevelChange"
    />
  </div>
</template>
